import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  getDocumentContent,
  type ClipboardDocument,
} from "@/features/clipboard/api/clipboard";
import {
  useClipboardAgentSearchQuery,
  useClipboardDocumentFilesQuery,
  useClipboardTreeQuery,
} from "@/features/clipboard/clipboardQueries";
import { RawFileRenderer } from "@/features/clipboard/components/RawFileRenderer";
import { useClipboardOpenPreview } from "@/features/clipboard/hooks/useClipboardOpenPreview";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { clipboardDocumentUrl } from "@/features/clipboard/utils/urls";
import { activateRowOnKey } from "@/utils/activateRowOnKey";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type VirtualRow = {
  name: string;
  kind: "file" | "folder" | "figure";
  fileType: string;
  detail?: string;
  childPath?: string;
  /** The user's uploaded file, as opposed to something we derived from it. */
  original?: boolean;
};

export const ClipboardDocumentFolder = ({
  onError,
}: {
  onError: (message: string) => void;
}) => {
  const { user } = useAuth();
  const autoPreviewDocumentRef = useRef<string | null>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    path,
    routeDocumentId,
    routeDocumentSubpath,
    routeSearchId,
    sharedOwnerId,
  } = useClipboardRoute();
  const {
    clipboardPreview,
    clipboardPreviewOpen,
    setClipboardPreview,
    setClipboardPreviewLoading,
    setInspectedDoc,
  } = useTreeViewOutletContext();
  const openPreview = useClipboardOpenPreview(onError);
  const filesQuery = useClipboardDocumentFilesQuery(
    routeDocumentId,
    sharedOwnerId,
    !!user,
  );
  const treeQuery = useClipboardTreeQuery(path, sharedOwnerId, !!user);
  const searchQuery = useClipboardAgentSearchQuery(routeSearchId, !!user);
  const siblingDocuments = useMemo(
    () =>
      routeSearchId
        ? searchQuery.data?.documents ?? []
        : treeQuery.data?.documents ?? [],
    [routeSearchId, searchQuery.data?.documents, treeQuery.data?.documents],
  );
  const listing = filesQuery.data;
  const document = listing?.document;
  const subpath = routeDocumentSubpath.replace(/^\/+|\/+$/g, "");
  const isLinesFile =
    subpath === "content.lines" || subpath.startsWith("sections/");
  const isMetaFile = subpath === "meta.json";
  const selectedEntry = listing?.entries.find((entry) => entry.name === subpath);
  const isImageFile =
    subpath.startsWith("figures/") || selectedEntry?.preview_kind === "image";
  const isRawFile = selectedEntry?.preview_kind === "raw";
  const directoryPath =
    isLinesFile || isImageFile || isMetaFile || isRawFile
      ? subpath.split("/").slice(0, -1).join("/")
      : subpath;

  const contentQuery = useQuery({
    queryKey: ["clipboard-document-file-content", routeDocumentId, sharedOwnerId],
    queryFn: () =>
      getDocumentContent(
        routeDocumentId!,
        5000,
        sharedOwnerId ?? undefined,
      ),
    enabled: !!user && !!routeDocumentId && isLinesFile,
  });
  useEffect(() => {
    if (!clipboardPreviewOpen) {
      autoPreviewDocumentRef.current = null;
    }
    if (document) {
      setInspectedDoc({
        ...(document as ClipboardDocument),
        folder_path:
          (document as ClipboardDocument).folder_path ?? path,
      });
      if (
        clipboardPreviewOpen &&
        clipboardPreview &&
        !clipboardPreview?.virtual_file &&
        clipboardPreview?.document.id !== document.id &&
        autoPreviewDocumentRef.current !== document.id
      ) {
        autoPreviewDocumentRef.current = document.id;
        void openPreview(document as ClipboardDocument);
      }
    }
  }, [
    clipboardPreview?.document.id,
    clipboardPreview?.virtual_file,
    clipboardPreviewOpen,
    document,
    openPreview,
    path,
    setInspectedDoc,
  ]);

  useEffect(() => {
    if (filesQuery.error) {
      onError(
        filesQuery.error instanceof Error
          ? filesQuery.error.message
          : "Could not open document folder",
      );
    }
  }, [filesQuery.error, onError]);

  const rows = useMemo<VirtualRow[]>(() => {
    if (!listing) return [];
    if (!directoryPath) {
      return listing.entries.map((entry) => ({
        name: entry.name,
        kind: entry.kind,
        fileType: entry.file_type,
        detail: entry.original
          ? "Original file"
          : entry.count != null
            ? `${entry.count}`
            : undefined,
        childPath: entry.name,
        original: entry.original,
      }));
    }
    if (directoryPath === "sections") {
      return listing.sections.map((section) => ({
        name: `${section.name}.lines`,
        kind: "file",
        fileType: "Lines",
        detail: `${section.line_count} line${section.line_count === 1 ? "" : "s"}`,
        childPath: `sections/${section.name}.lines`,
      }));
    }
    if (directoryPath === "figures") {
      return listing.figures.map((figure) => ({
        name: figure.name,
        kind: "figure",
        fileType: "Image",
        detail: figure.page ? `Page ${figure.page}` : figure.original ? "Original" : undefined,
        childPath: `figures/${figure.name}`,
      }));
    }
    return [];
  }, [directoryPath, listing]);
  const displayedLines = useMemo(() => {
    const lines = contentQuery.data?.lines ?? [];
    const sectionName = subpath.startsWith("sections/")
      ? subpath.slice("sections/".length).replace(/\.lines$/, "")
      : null;
    return lines.filter((line) => !sectionName || line.section === sectionName);
  }, [contentQuery.data?.lines, subpath]);

  const displayMetadata = useMemo(() => {
    if (!document) return {};
    const visibleKeys = [
      "title",
      "filename",
      "page_count",
      "file_size_bytes",
      "source_type",
      "match_type",
      "link_type",
      "corpus_doc_id",
      "created_at",
      "updated_at",
      "content_status",
      "index_status",
      "citation",
      "corpus_meta",
    ];
    return Object.fromEntries(
      visibleKeys
        .filter((key) => document[key] != null)
        .map((key) => [key, document[key]]),
    );
  }, [document]);

  const previewVirtualDocumentId = clipboardPreview?.virtual_file
    ? clipboardPreview.document.id
    : null;

  useEffect(() => {
    if (
      document &&
      !isMetaFile &&
      !isLinesFile &&
      !isImageFile &&
      !isRawFile &&
      previewVirtualDocumentId === document.id
    ) {
      setClipboardPreview(null);
    }
  }, [
    document,
    isImageFile,
    isLinesFile,
    isMetaFile,
    isRawFile,
    previewVirtualDocumentId,
    setClipboardPreview,
  ]);

  useEffect(() => {
    if (!document || !isImageFile) {
      if (document && (isMetaFile || isLinesFile || isRawFile)) {
        setClipboardPreview(null);
      }
      setClipboardPreviewLoading(false);
      return;
    }
    setClipboardPreview({
      document,
      lines: [],
      total_lines: 0,
      truncated: false,
      virtual_file: {
        path: subpath,
        name: subpath.startsWith("figures/")
          ? subpath.slice("figures/".length)
          : subpath,
        kind: "image",
      },
    });
    setClipboardPreviewLoading(false);
  }, [
    document,
    isImageFile,
    isLinesFile,
    isMetaFile,
    isRawFile,
    location.key,
    setClipboardPreview,
    setClipboardPreviewLoading,
    subpath,
  ]);

  const goToChild = useCallback(
    (childPath: string) => {
      if (!routeDocumentId) return;
      navigate(
        clipboardDocumentUrl(
          routeDocumentId,
          path,
          sharedOwnerId,
          routeSearchId,
          childPath,
        ),
      );
    },
    [
      navigate,
      path,
      routeDocumentId,
      routeSearchId,
      sharedOwnerId,
    ],
  );

  useEffect(() => {
    const handleArrowNavigation = (event: KeyboardEvent) => {
      if (
        (event.key !== "ArrowUp" && event.key !== "ArrowDown") ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          'input, textarea, select, button, [contenteditable="true"]',
        )
      ) {
        return;
      }

      if (!subpath && routeDocumentId) {
        const currentIndex = siblingDocuments.findIndex(
          (item) => item.id === routeDocumentId,
        );
        if (currentIndex < 0) return;
        event.preventDefault();
        const offset = event.key === "ArrowDown" ? 1 : -1;
        const nextDocument = siblingDocuments[currentIndex + offset];
        if (!nextDocument) return;

        const documentPath = nextDocument.folder_path ?? path;
        navigate(
          clipboardDocumentUrl(
            nextDocument.id,
            documentPath,
            sharedOwnerId,
            routeSearchId,
          ),
        );
        return;
      }

      const selectableRows = rows.filter(
        (row) => row.kind !== "folder" && row.childPath,
      );
      const currentIndex = selectableRows.findIndex(
        (row) => row.childPath === subpath,
      );
      if (currentIndex < 0) return;

      event.preventDefault();
      const offset = event.key === "ArrowDown" ? 1 : -1;
      const nextRow = selectableRows[currentIndex + offset];
      if (!nextRow?.childPath) return;

      goToChild(nextRow.childPath);
    };

    window.addEventListener("keydown", handleArrowNavigation);
    return () => window.removeEventListener("keydown", handleArrowNavigation);
  }, [
    goToChild,
    navigate,
    path,
    routeDocumentId,
    routeSearchId,
    rows,
    sharedOwnerId,
    siblingDocuments,
    subpath,
  ]);

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [subpath]);

  if (filesQuery.isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-base italic text-zinc-400">
        Opening document…
      </div>
    );
  }

  if (!listing || !document) return <div className="min-h-0 flex-1" />;

  const isCorpusLink = document.link_type === "corpus_link";
  const originalEntry = listing.entries.find((entry) => entry.original);

  let body: React.ReactNode;
  if (isMetaFile) {
    body = (
      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-8 py-6">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-6 text-zinc-700">
          {JSON.stringify(displayMetadata, null, 2)}
        </pre>
      </div>
    );
  } else if (isLinesFile) {
    body = contentQuery.isLoading ? (
      <div className="flex min-h-0 flex-1 items-center justify-center text-base italic text-zinc-400">
        Loading content…
      </div>
    ) : contentQuery.error ? (
      <div className="min-h-0 flex-1 px-8 py-6 text-base text-red-700" role="alert">
        Could not load document content.
      </div>
    ) : (
      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-8 py-6">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-6 text-zinc-700">
          {displayedLines.map((line) => line.content).join("\n")}
        </pre>
      </div>
    );
  } else if (isRawFile) {
    body = (
      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-8 py-6">
        <RawFileRenderer docId={document.id} filename={subpath} />
      </div>
    );
  } else {
    body = (
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">
        <table className="w-full min-w-[42rem] table-fixed text-left text-base leading-6">
          <thead className="sticky top-0 z-10 border-b border-zinc-200 bg-[var(--color-white,#fff)] text-2xs uppercase tracking-[0.18em] text-zinc-400">
            <tr>
              <th className="w-[60%] px-8 py-2 font-normal">Name</th>
              <th className="w-[20%] px-3 py-2 font-normal">Type</th>
              <th className="w-[20%] px-8 py-2 font-normal">Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              return (
                <tr
                  key={row.name}
                  ref={row.childPath === subpath ? selectedRowRef : undefined}
                  aria-selected={row.childPath === subpath}
                  tabIndex={row.childPath ? 0 : undefined}
                  className={`group cursor-pointer border-b border-zinc-200/70 transition-colors hover:bg-zinc-900/[0.025] focus-visible:outline-none focus-visible:bg-zinc-900/[0.025] ${row.childPath === subpath ? "bg-zinc-900/[0.04]" : ""
                    }`}
                  onClick={() => {
                    if (row.childPath) {
                      goToChild(row.childPath);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (row.childPath) {
                      activateRowOnKey(event, () => goToChild(row.childPath!));
                    }
                  }}
                >
                  <td className="overflow-hidden px-8 py-2.5">
                    <span className={`block truncate ${row.original ? "italic text-zinc-700" : "text-zinc-900"}`}>
                      {row.name}
                      {row.kind === "folder" && <span className="text-zinc-400">/</span>}
                    </span>
                  </td>
                  <td className="truncate px-3 py-2.5 text-sm text-zinc-500">{row.fileType}</td>
                  <td className="truncate px-8 py-2.5 text-sm text-zinc-400">
                    {row.detail ?? "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Persistent reminder that this "folder" is really a single file. */}
      <div className="flex flex-wrap items-baseline gap-2 px-8 pb-3 text-sm leading-6 text-zinc-500">
        <span className="min-w-0">
          <span className="text-zinc-800">
            {String(document.filename || document.title || "This document")}
          </span>
          {isCorpusLink
            ? " is a linked paper; you're browsing its parsed contents."
            : " is a single file; you're browsing its parsed contents."}
        </span>
        {originalEntry && subpath !== originalEntry.name && (
          <button
            type="button"
            onClick={() => goToChild(originalEntry.name)}
            title="Open the file you uploaded, not the parsed version"
            className="ml-auto shrink-0 text-zinc-400 transition-colors hover:text-zinc-900"
          >
            Open original
          </button>
        )}
      </div>
      {body}
    </div>
  );
};
